/**
 *
 * TextFieldMUI
 *
 * TextField not editable used as Text Label
 */

import TextFieldMUI from '@mui/material/TextField';
import { styled } from '@mui/material/styles';

const StyledInfoTextField = styled(TextFieldMUI, {
  shouldForwardProp: (prop) => prop !== 'infoColor' && prop !== 'infoWidth'
})(({ infoColor, infoWidth }) => ({
  '& #calculator': {
    width: '30%',
    marginBottom: '0vh',
    marginRight: '0vh',
    fontSize: '1em'
  },
  '& #small-calculator': {
    width: '30%',
    marginBottom: '0vh',
    marginRight: '0vh',
    fontSize: '0.8rem'
  },
  '& #long-info': {
    fontSize: 'smaller',
    color: 'black',
    fontWeight: '600',
    textShadow: '1px 1px 1px white',
    cursor: 'help',
    margin: ' -3px 0px -8px 0px'
  },
  '& #smallInfo': {
    width: '100px',
    fontSize: '1em',
    fontWeight: 'bold',
    textShadow: '1px 1px 1px white',
    cursor: 'help',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    borderRadius: '0.4rem'
  },
  '& #drying': {
    width: '100%',
    fontSize: '1vmin',
    color: 'white',
    fontWeight: '600',
    textShadow: '1px 1px 1px black',
    cursor: 'help'
  },
  '& #info-label': {
    width: '25vw',
    fontSize: '1em',
    marginBottom: '0vh',
    marginRight: '0vh',
    marginLeft: '0vw',
    color: 'black',
    fontWeight: '',
    height: '1.2em'
  },
  '& #lineBoxInfo': {
    width: '100%',
    fontSize: '1em',
    fontWeight: 'bold',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    borderRadius: '0.4rem',
    textAlign: 'center',
    color: infoColor
  },
  '& #fiberInfo': {
    height: '12px',
    width: '100%',
    fontSize: '10px',
    fontWeight: 'bold',
    color: 'white',
    cursor: 'none',
    borderRadius: '0.4rem',
    textAlign: 'left'
  },
  '& #layerOrientation': {
    fontSize: '1.5vh',
    fontWeight: 'normal',
    width: 'auto'
  },
  '& #packageInfo': {
    fontSize: '1vh',
    fontWeight: 'bold',
    width: infoWidth,
    background: 'none'
  },
  '& #longlagerInfo': {
    fontSize: '1vh',
    fontWeight: 'bold',
    maxWidth: '95%'
  },
  '& #editForm': {
    width: '100%',
    fontSize: '1em',
    fontWeight: 'bold',
    borderRadius: '0.4rem',
    textAlign: 'center',
    color: infoColor,
    borderColor: 'red'
  },
  '& .MuiOutlinedInput-input': {
    padding: '12.5px 4px'
  },
  '& .MuiInput-root': {
    fontFamily: 'Poppins',
    fontWeight: '500',
    letterSpacing: 'normal',
    lineHeight: '1em'
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Poppins',
    lineHeight: '1em',
    color: 'black'
  },
  '& label.Mui-focused': {
    color: 'black',
    width: '110%'
  },
  '& .MuiInput-underline:after': {
    border: '0em  black'
  },
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Poppins',
    '& fieldset': {
      borderColor: 'green'
    },
    '&:hover fieldset': {
      borderColor: '#f9b115'
    },
    '&.Mui-focused fieldset': {
      border: '0.1em solid gray'
    }
  }
}));

export const InfoTextField = ({ color: customColor, width: customWidth, ...rest }) => (
  <StyledInfoTextField hiddenLabel color="primary" autoComplete="off" infoColor={customColor} infoWidth={customWidth} {...rest} />
);

const StyledDryingField = styled(TextFieldMUI)(() => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '1vmin',
    color: 'white',
    fontFamily: 'Poppins'
  },
  '& .MuiOutlinedInput-input': {
    paddingBottom: '2px',
    paddingLeft: '3px',
    paddingRight: '3px',
    paddingTop: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)'
  }
}));

export const DryingField = (props) => (
  <StyledDryingField
    {...props}
    size="small"
    sx={{
      fontSize: '8px'
    }}
  />
);

const StyledLineBoxInfo = styled(TextFieldMUI)(() => ({
  '& .MuiInput-root': {
    width: '75%',
    fontSize: '1em',
    fontWeight: 'bold',
    cursor: 'none',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    borderRadius: '0.4rem',
    textAlign: 'center',
    marginBottom: '10px',
    padding: '5px'
  },
  '& .MuiInputLabel-root': {
    fontSize: '1.3em',
    lineHeight: '1em'
  },
  '& label.Mui-focused': {
    color: 'black'
  },
  '&.Mui-focused fieldset': {
    border: '0.1em solid gray'
  }
}));

export const LineBoxInfo = (props) => (
  <StyledLineBoxInfo
    {...props}
    size="small"
    hiddenLabel
    color="primary"
    variant="filled"
    sx={{
      fontSize: '10px'
    }}
  />
);

const StyledLayerInfo = styled(TextFieldMUI)(() => ({
  '& .MuiInput-root': {
    fontSize: '1.3vh',
    fontWeight: 'bold',
    width: 'auto'
  },
  '& .MuiInputLabel-root': {
    fontSize: '1.3em',
    lineHeight: '1em'
  },
  '& label.Mui-focused': {
    color: 'black'
  },
  '&.Mui-focused fieldset': {
    border: '0.1em solid gray'
  }
}));

export const LayerInfo = (props) => <StyledLayerInfo {...props} size="small" hiddenLabel color="primary" />;

const StyledPressStackerField = styled(TextFieldMUI)(() => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '1vh',
    fontWeight: 'bold',
    maxWidth: '95%'
  }
}));

export const PressStackerField = (props) => (
  <StyledPressStackerField
    {...props}
    size="small"
    sx={{
      fontSize: '10px'
    }}
  />
);
